import {z} from 'zod';

// ============================================================================
// Per-Course Notification Settings
// ============================================================================
// Schema and pure-derivation helpers for the per-course notification
// preferences doc at `users/{userId}/{courseId}/notificationSettings`.
//
// Three fields live on the doc:
//
// - `emailLists`: the existing array of bulk-list subscription keys
//   (`forum.{forumId}.{post|reply}.{public|private}`). Managed by users via
//   `NotifSettings.tsx`. Drives the `sync_notification_lists` trigger which
//   mirrors entries into per-list `notificationLists.userList` aggregates
//   for the bulk-path recipient lookup.
//
// - `replyToMyPostEnabled`: the explicit per-channel toggle for the
//   pinpoint "someone replied to a post or reply you wrote" email path.
//   When present, it's the sole signal the trigger uses for that path.
//   When absent, the trigger defaults to send — matching the historical
//   pre-#3220 baseline. The Phase 2 migration (per the #3235 dev plan)
//   stamps an explicit value for every existing doc so the trigger never
//   needs an implicit-default fallback in production. See #3235.
//
// - `appreciationStudentEmailsEnabled`: whether a section leader gets an
//   email for each appreciation note a student sends them. Absent means
//   send. Turning it off holds the email only; the note is still saved.
//   Read by the `sendappreciation` callable. See #5591.
//
// SDK-free so it can be imported into unit tests without pulling
// firebase-admin (which trips deployment-safety lints).
// ============================================================================

/**
 * Per-course user notification preferences. Stored at
 * `users/{userId}/{courseId}/notificationSettings`.
 *
 * `.loose()` so undocumented fields written by other consumers pass through
 * unchanged.
 */
export const NotificationSettingsSchema = z
  .object({
    /**
     * Array of bulk-list subscription keys the user has opted into via
     * `NotifSettings.tsx` (e.g. `forum.main.reply.public`,
     * `forum.*.reply.public`). The `sync_notification_lists` trigger
     * mirrors this array into per-list `notificationLists.userList`
     * aggregates for the bulk-path recipient lookup. Optional because
     * legacy docs predate the field.
     */
    emailLists: z.array(z.string()).optional(),
    /**
     * Explicit opt-in (`true`) or opt-out (`false`) for the pinpoint
     * "someone replied to a post you wrote" email path. The sole signal
     * the trigger uses for that path. Absent → default to send (matches
     * the pre-#3220 baseline; the Phase 2 migration stamps an explicit
     * value for every existing doc so the absent branch is reserved for
     * brand-new users with no `notificationSettings` doc at all). See
     * #3235.
     */
    replyToMyPostEnabled: z.boolean().optional(),
    /**
     * Explicit opt-in (`true`) or opt-out (`false`) for the email a section
     * leader gets for each appreciation note a student sends them in this
     * course. Absent → default to send. `false` holds the email only: the
     * note is still saved, and no in-app view shows it to the recipient.
     * Notes from staff always email. See #5591.
     */
    appreciationStudentEmailsEnabled: z.boolean().optional(),
  })
  .loose();

export type NotificationSettings = z.infer<typeof NotificationSettingsSchema>;

/**
 * Document path for a user's per-course notification settings.
 *
 * @param params.userId - Firebase Auth uid.
 * @param params.courseId - Course identifier; the subcollection name under
 *   `users/{userId}/`.
 * @return Firestore document path suitable for `firestore.doc(...)`.
 */
export function getNotificationSettingsPath({
  userId,
  courseId,
}: {
  userId: string;
  courseId: string;
}): string {
  return `users/${userId}/${courseId}/notificationSettings`;
}

/**
 * Decides whether the pinpoint reply email should be sent for a given
 * recipient, given their `notificationSettings` doc data (or `undefined`
 * if the doc doesn't exist).
 *
 * Simple gate, single field:
 * - `replyToMyPostEnabled === true` → send.
 * - `replyToMyPostEnabled === false` → skip.
 * - Absent → send. Matches the pre-#3220 baseline; in production the
 *   Phase 2 migration stamps an explicit value for every existing doc,
 *   so the absent branch is reserved for brand-new users.
 *
 * Does not check the global `okToEmail` flag — callers should still
 * short-circuit on that before invoking this.
 *
 * @param data - The `notificationSettings` doc payload, or `undefined` if
 *   the doc doesn't exist.
 * @return `true` to send, `false` to suppress.
 */
export function shouldSendReplyEmailToParentAuthor(
  data: NotificationSettings | undefined,
): boolean {
  if (data?.replyToMyPostEnabled === false) {
    return false;
  }
  return true;
}

/**
 * Decides whether a section leader should be emailed an appreciation note a
 * student sent them, given their `notificationSettings` doc data (or
 * `undefined` if the doc doesn't exist).
 *
 * - `appreciationStudentEmailsEnabled === false` → hold the email.
 * - `true` or absent → send.
 *
 * Applies only to notes from students; callers must not consult it for
 * staff senders.
 *
 * @param data - The `notificationSettings` doc payload, or `undefined` if
 *   the doc doesn't exist.
 * @return `true` to send the email, `false` to hold it.
 * @throws Never.
 */
export function shouldEmailStudentAppreciation(
  data: NotificationSettings | undefined,
): boolean {
  return data?.appreciationStudentEmailsEnabled !== false;
}
